/**
 * Minimal admin-side LiveKit subscriber. Connects to a device's room and lets
 * the camera manager attach the device participant's video track by trackName
 * (the operator-assigned camera name), and read live inbound-rtp stats
 * (resolution / fps / bitrate) for each track.
 *
 * Subscriber-only: the admin token grants canSubscribe; nothing is published.
 */
import {
  Room,
  RoomEvent,
  Track,
  type RemoteTrack,
  type RemoteTrackPublication,
  type RemoteParticipant,
} from "livekit-client";

/** The device participant's LiveKit identity is prefixed with this. */
export const DEVICE_IDENTITY_PREFIX = "device:";

export interface IceServer {
  urls: string | string[];
  username?: string;
  credential?: string;
}

export interface TrackStats {
  /** Frame width in px, or null when unavailable. */
  width: number | null;
  height: number | null;
  /** Frames per second (from RTCInboundRtpStreamStats.framesPerSecond). */
  fps: number | null;
  /** Receive bitrate in kbps, computed from a bytesReceived delta. */
  kbps: number | null;
}

interface StatsSample {
  bytesReceived: number;
  ts: number;
}

export interface AdminSubscriberOptions {
  iceServers?: IceServer[];
}

export class AdminSubscriberClient {
  readonly room: Room;
  /** trackName → latest computed stats. */
  private stats = new Map<string, TrackStats>();
  /** trackName → previous raw sample (for bitrate delta). */
  private samples = new Map<string, StatsSample>();
  private onTracksChanged?: () => void;

  constructor(
    opts: AdminSubscriberOptions = {},
    onTracksChanged?: () => void,
  ) {
    this.onTracksChanged = onTracksChanged;
    this.room = new Room({
      adaptiveStream: false,
      dynacast: false,
      ...(opts.iceServers && opts.iceServers.length > 0
        ? { rtcConfig: { iceServers: opts.iceServers as RTCIceServer[] } }
        : {}),
    });

    this.room.on(
      RoomEvent.TrackSubscribed,
      (
        _track: RemoteTrack,
        _pub: RemoteTrackPublication,
        _participant: RemoteParticipant,
      ) => {
        this.onTracksChanged?.();
      },
    );
    this.room.on(
      RoomEvent.TrackUnsubscribed,
      (
        _track: RemoteTrack,
        pub: RemoteTrackPublication,
        _participant: RemoteParticipant,
      ) => {
        if (pub.trackName) {
          this.stats.delete(pub.trackName);
          this.samples.delete(pub.trackName);
        }
        this.onTracksChanged?.();
      },
    );
  }

  async connect(url: string, token: string): Promise<void> {
    await this.room.connect(url, token, { autoSubscribe: true });
  }

  async disconnect(): Promise<void> {
    try {
      await this.room.disconnect();
    } catch {
      // best-effort
    }
  }

  /** The device participant (identity prefixed `device:`), or null. */
  private deviceParticipant(): RemoteParticipant | null {
    for (const p of this.room.remoteParticipants.values()) {
      if (p.identity.startsWith(DEVICE_IDENTITY_PREFIX)) return p;
    }
    return null;
  }

  /**
   * Find the device participant's video publication whose trackName matches
   * `trackName`, and attach its track to `el`. Returns true when attached.
   */
  attachTrack(trackName: string, el: HTMLVideoElement): boolean {
    const dev = this.deviceParticipant();
    if (!dev) return false;
    for (const pub of dev.trackPublications.values()) {
      if (pub.kind !== Track.Kind.Video) continue;
      if (pub.trackName !== trackName) continue;
      const track = pub.track;
      if (track) {
        track.attach(el);
        return true;
      }
    }
    return false;
  }

  /** Detach any tracks bound to the given element. */
  detach(el: HTMLVideoElement): void {
    for (const p of this.room.remoteParticipants.values()) {
      for (const pub of p.trackPublications.values()) {
        pub.track?.detach(el);
      }
    }
  }

  /** Latest computed stats for a trackName, or null. */
  getStats(trackName: string): TrackStats | null {
    return this.stats.get(trackName) ?? null;
  }

  /**
   * Poll RTCStats for every subscribed device video track and recompute
   * resolution/fps/bitrate. Bitrate is derived from the bytesReceived delta
   * between successive polls (inbound-rtp on the subscriber side).
   */
  async pollStats(): Promise<void> {
    const dev = this.deviceParticipant();
    if (!dev) return;
    for (const pub of dev.trackPublications.values()) {
      if (pub.kind !== Track.Kind.Video) continue;
      const name = pub.trackName;
      if (!name) continue;
      const receiver = pub.track?.receiver;
      if (!receiver) continue;
      let report: RTCStatsReport;
      try {
        report = await receiver.getStats();
      } catch {
        continue;
      }
      let bytesReceived: number | null = null;
      let fps: number | null = null;
      let width: number | null = null;
      let height: number | null = null;
      report.forEach((s) => {
        const stat = s as Record<string, unknown>;
        if (stat.type === "inbound-rtp" && stat.kind === "video") {
          if (typeof stat.bytesReceived === "number")
            bytesReceived = stat.bytesReceived;
          if (typeof stat.framesPerSecond === "number")
            fps = stat.framesPerSecond;
          if (typeof stat.frameWidth === "number") width = stat.frameWidth;
          if (typeof stat.frameHeight === "number") height = stat.frameHeight;
        }
      });
      const now = Date.now();
      let kbps: number | null = null;
      if (bytesReceived !== null) {
        const prev = this.samples.get(name);
        if (prev) {
          const dt = (now - prev.ts) / 1000;
          if (dt > 0) {
            const dBytes = bytesReceived - prev.bytesReceived;
            kbps = Math.max(0, Math.round((dBytes * 8) / 1000 / dt));
          }
        }
        this.samples.set(name, { bytesReceived, ts: now });
      }
      this.stats.set(name, { width, height, fps, kbps });
    }
  }
}
